import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { DateInput } from '@/components/ui/DateInput'
import { useCreateExchangeRate } from '@/features/tax-rules/hooks/useTaxRules'
import type { MissingExchangeRateRequirement } from '@/features/payroll/types/payroll.types'

interface CreateExchangeRateModalValues {
  baseCurrency: string
  targetCurrency: string
  effectiveDate: string
  rate: string
  source: string
}

export interface CreateExchangeRateModalProps {
  requirement: MissingExchangeRateRequirement | null
  open: boolean
  onClose: () => void
  onCreated?: () => void
}

function toDateInputValue(asOf: string): string {
  return asOf.slice(0, 10)
}

export function CreateExchangeRateModal({
  requirement,
  open,
  onClose,
  onCreated,
}: CreateExchangeRateModalProps): React.ReactElement | null {
  const createExchangeRate = useCreateExchangeRate()
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateExchangeRateModalValues>({
    defaultValues: {
      baseCurrency: '',
      targetCurrency: '',
      effectiveDate: '',
      rate: '',
      source: 'manual',
    },
  })

  useEffect(() => {
    if (!open || !requirement) return
    reset({
      baseCurrency: requirement.fromCurrency,
      targetCurrency: requirement.toCurrency,
      effectiveDate: toDateInputValue(requirement.asOf),
      rate: '',
      source: 'manual',
    })
  }, [open, requirement, reset])

  if (!open || !requirement) return null

  const onSubmit = (values: CreateExchangeRateModalValues): void => {
    createExchangeRate.mutate(
      {
        baseCurrency: values.baseCurrency,
        targetCurrency: values.targetCurrency,
        rate: Number(values.rate),
        effectiveDate: values.effectiveDate,
        source: values.source || 'manual',
      },
      {
        onSuccess: () => {
          onCreated?.()
          onClose()
        },
      },
    )
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add exchange rate"
      className="max-w-lg"
      footer={
        <>
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={createExchangeRate.isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="create-exchange-rate-form"
            variant="primary"
            loading={createExchangeRate.isPending}
          >
            {createExchangeRate.isPending ? 'Saving…' : 'Save rate'}
          </Button>
        </>
      }
    >
      <form
        id="create-exchange-rate-form"
        onSubmit={handleSubmit(onSubmit)}
        className="space-y-4"
      >
        <div className="grid gap-4 md:grid-cols-2">
          <Input
            label="Base currency"
            {...register('baseCurrency', { required: 'Base currency is required' })}
            error={errors.baseCurrency?.message}
            readOnly
          />
          <Input
            label="Target currency"
            {...register('targetCurrency', { required: 'Target currency is required' })}
            error={errors.targetCurrency?.message}
            readOnly
          />
          <DateInput
            label="Effective date"
            {...register('effectiveDate', { required: 'Effective date is required' })}
            error={errors.effectiveDate?.message}
          />
          <Input
            label="Rate"
            type="number"
            step="0.000001"
            min="0"
            inputMode="decimal"
            {...register('rate', {
              required: 'Rate is required',
              validate: (value) =>
                Number(value) > 0 || 'Rate must be greater than zero',
            })}
            error={errors.rate?.message}
            placeholder="e.g. 0.0036"
          />
        </div>
        <Input
          label="Source"
          {...register('source')}
          placeholder="manual"
        />
      </form>
    </Modal>
  )
}

CreateExchangeRateModal.displayName = 'CreateExchangeRateModal'
